import { Episode, MediaLink } from '@/types/model';

const placeholder = (character: 's' | 'e', width: number) => (
  `[${character.repeat(Math.max(1, Math.min(width, character === 's' ? 2 : 3)))}]`
);

const matchesNumber = (value: string, expected: number) => Number(value) === expected;

const replaceSeasonPathSegment = (
  url: string,
  seasonNumber: number,
) => {
  const template = url.replace(
    /(^|\/)([st])(\d{1,2})(?=\/|[?#]|$)/gi,
    (match, separator: string, prefix: string, digits: string) => (
      matchesNumber(digits, seasonNumber)
        ? `${separator}${prefix}${placeholder('s', digits.length)}`
        : match
    ),
  );

  const numericSegmentPattern = /(^|\/)(\d{1,2})(?=\/)/g;
  let numericSegment: RegExpExecArray | null = null;
  let match = numericSegmentPattern.exec(template);
  while (match) {
    if (matchesNumber(match[2], seasonNumber)) numericSegment = match;
    match = numericSegmentPattern.exec(template);
  }

  if (!numericSegment) return template;

  const digitsStart = numericSegment.index + numericSegment[1].length;
  return `${template.slice(0, digitsStart)}`
    + `${placeholder('s', numericSegment[2].length)}`
    + `${template.slice(digitsStart + numericSegment[2].length)}`;
};

export const deriveEpisodeLinkTemplate = (
  url: string,
  seasonNumber: number,
  episodeNumber: number,
): string | null => {
  const seasonEpisodePattern = /(s)(\d{1,2})([._ -]*)(e)(\d{1,3})/i;
  const seasonEpisodeMatch = seasonEpisodePattern.exec(url);
  if (
    seasonEpisodeMatch
    && matchesNumber(seasonEpisodeMatch[2], seasonNumber)
    && matchesNumber(seasonEpisodeMatch[5], episodeNumber)
  ) {
    return replaceSeasonPathSegment(
      url.replace(
        seasonEpisodePattern,
        `${seasonEpisodeMatch[1]}${placeholder('s', seasonEpisodeMatch[2].length)}`
        + `${seasonEpisodeMatch[3]}${seasonEpisodeMatch[4]}`
        + `${placeholder('e', seasonEpisodeMatch[5].length)}`,
      ),
      seasonNumber,
    );
  }

  const xPattern = /(\d{1,2})(x)(\d{1,3})/i;
  const xMatch = xPattern.exec(url);
  if (
    xMatch
    && matchesNumber(xMatch[1], seasonNumber)
    && matchesNumber(xMatch[3], episodeNumber)
  ) {
    return replaceSeasonPathSegment(
      url.replace(
        xPattern,
        `${placeholder('s', xMatch[1].length)}${xMatch[2]}${placeholder('e', xMatch[3].length)}`,
      ),
      seasonNumber,
    );
  }

  let episodeMatched = false;
  let template = url.replace(/(e)(\d{1,3})/i, (match, prefix: string, digits: string) => {
    if (!matchesNumber(digits, episodeNumber)) return match;
    episodeMatched = true;
    return `${prefix}${placeholder('e', digits.length)}`;
  });
  if (!episodeMatched) {
    template = url.replace(/((?:episode|ep)[._ -]*)(\d{1,3})/i, (match, prefix: string, digits: string) => {
      if (!matchesNumber(digits, episodeNumber)) return match;
      episodeMatched = true;
      return `${prefix}${placeholder('e', digits.length)}`;
    });
  }
  if (!episodeMatched) return null;

  template = replaceSeasonPathSegment(template, seasonNumber);

  return template.replace(
    /(s)(\d{1,2})/i,
    (match, prefix: string, digits: string) => (
      matchesNumber(digits, seasonNumber)
        ? `${prefix}${placeholder('s', digits.length)}`
        : match
    ),
  );
};

export const fillEpisodeLinkTemplate = (
  template: string,
  seasonNumber: number,
  episodeNumber: number,
) => template
  .replace(/\[ss\]/g, String(seasonNumber).padStart(2, '0'))
  .replace(/\[s\]/g, String(seasonNumber))
  .replace(/\[eee\]/g, String(episodeNumber).padStart(3, '0'))
  .replace(/\[ee\]/g, String(episodeNumber).padStart(2, '0'))
  .replace(/\[e\]/g, String(episodeNumber));

const linksByPreference = (links: MediaLink[] = []) => (
  [...links].sort((left, right) => Number(right.active) - Number(left.active))
);

const episodesBefore = (
  episodes: Episode[],
  seasonNumber?: number,
  episodeNumber?: number,
) => episodes
  .filter((episode) => (
    seasonNumber == null
    || episode.seasonNumber < seasonNumber
    || (episode.seasonNumber === seasonNumber && episode.episodeNumber < (episodeNumber ?? 0))
  ))
  .sort((left, right) => (
    right.seasonNumber - left.seasonNumber || right.episodeNumber - left.episodeNumber
  ));

export const inferEpisodeLinkTemplate = (episodes: Episode[]): string => {
  for (const episode of episodesBefore(episodes)) {
    for (const link of linksByPreference(episode.links)) {
      const template = deriveEpisodeLinkTemplate(
        link.url,
        episode.seasonNumber,
        episode.episodeNumber,
      );
      if (template) return template;
    }
  }
  return '';
};

export interface PreviousEpisodeLinkSuggestion {
  linkDefaults: Pick<
    MediaLink,
    'quality' | 'audioLanguages' | 'subtitleLanguages'
  >;
  url: string;
}

export const suggestPreviousEpisodeLink = (
  episodes: Episode[],
  seasonNumber: number,
  episodeNumber: number,
): PreviousEpisodeLinkSuggestion | null => {
  for (const episode of episodesBefore(episodes, seasonNumber, episodeNumber)) {
    for (const link of linksByPreference(episode.links)) {
      const template = deriveEpisodeLinkTemplate(
        link.url,
        episode.seasonNumber,
        episode.episodeNumber,
      );
      if (template) {
        return {
          linkDefaults: {
            quality: link.quality,
            audioLanguages: link.audioLanguages,
            subtitleLanguages: link.subtitleLanguages,
          },
          url: fillEpisodeLinkTemplate(template, seasonNumber, episodeNumber),
        };
      }
    }
  }
  return null;
};
